import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Aviso, Carregando, LogoCF } from "../componentes/ui";
import SuporteAlunos from "../componentes/SuporteAlunos";
import SuporteTriagem from "../componentes/SuporteTriagem";
import AdminFaculdade from "../componentes/AdminFaculdade";
import AdminPosDocentes from "../componentes/AdminPosDocentes";
import AdminCatalogo from "../componentes/AdminCatalogo";
import AdminFeedbacks from "../componentes/AdminFeedbacks";
import AdminCursosGratuitos from "../componentes/AdminCursosGratuitos";
import AdminCorrecaoCfc from "../componentes/AdminCorrecaoCfc";
import AdminSentinela from "../componentes/AdminSentinela";
import AdminConteudo from "../componentes/AdminConteudo";
import AdminOcupacoes from "../componentes/AdminOcupacoes";
import AdminParametros from "../componentes/AdminParametros";
import AdminCalendario from "../componentes/AdminCalendario";
import AdminLegaliza from "../componentes/AdminLegaliza";
import AdminConcursos from "../componentes/AdminConcursos";
import AdminForoFacil from "../componentes/AdminForoFacil";
import AdminRadar from "../componentes/AdminRadar";
import { horaDoDado, nivelNaAba, numero, useOperador, usePainel, type Operador } from "../lib/admin";
import AdminPermissoes from "../componentes/AdminPermissoes";
import AdminSugestoes from "../componentes/AdminSugestoes";

/**
 * O painel de operação. Quem entra, e em que aba, sai dos GRUPOS de
 * Admin › Permissões (000297): o papel Suporte morreu e o admin sobreviveu só
 * como master.
 *
 * ## Por que navy no topo e claro no corpo
 *
 * O topo é navy para dizer, sem texto, "você saiu da área do aluno". O operador
 * aqui é a mesma pessoa que também é aluno (o infra@ é os dois), e confundir as
 * duas telas é como se apaga acesso de gente de verdade por engano.
 *
 * O corpo é claro porque o que vem depois desta primeira versão é tabela e
 * formulário: lista de triagem, busca de aluno, edição de cadastro. Dado denso
 * em fundo escuro se lê pior, e — a razão técnica — sobre navy a cor semântica é
 * armadilha: `text-tinta` clareia no escuro e vira branco sobre branco. Já
 * aconteceu nos cards do login. Fazendo o painel inteiro navy, toda tela nova
 * herdaria essa pegadinha.
 *
 * ## O que NÃO existe aqui
 *
 * Nenhuma decisão de acesso em JavaScript. O guard abaixo redireciona quem não é
 * operador, mas isso é conforto de navegação, não segurança: quem recusa de
 * verdade é o banco, que devolve zero linha para quem não passa por
 * `eh_operador()`, e cada função pergunta `pode_ver`/`pode_editar` da aba dela.
 * Se este arquivo inteiro fosse burlado no navegador, o painel viria vazio.
 */

type Area = "suporte" | "admin";

type Aba = {
  id: string;
  rotulo: string;
  area: Area;
  /** O que a aba faz, em uma frase — é o que aparece enquanto ela não existe. */
  faz: string;
  /** O que falta para ela existir. Escrito para ser lido por quem for construir. */
  espera?: string;
  itens?: string[];
  /**
   * Aba pronta que está FORA DO AR por decisão, e não por falta de construção.
   *
   * Some da navegação **e** da rota: quem digitar `/admin/<id>` volta ao painel.
   * As duas coisas juntas, porque esconder só do menu deixa a tela alcançável
   * por quem tem o link — e "oculta" só é verdade quando também é inalcançável.
   *
   * É diferente de não ter entrada em `CONTEUDO`, que é o estado "ainda não
   * construída" e mostra o que falta. Aqui a tela existe e funciona; o que se
   * desliga é o acesso a ela.
   *
   * O texto diz POR QUE está desligada e quem decidiu — sem isso, a próxima
   * pessoa acha que é bug e religa.
   */
  oculta?: string;
  /**
   * A aba mora num item do menu com SUBMENU, e não solta na barra. Pedido de
   * 30/09: as oito abas que configuram uma ferramenta do aluno (Conteúdo,
   * Ocupações, Parâmetros, Calendário, Legalização, Concursos, ForoFácil e Radar
   * Simples) viraram um item «Ferramentas», que abre a lista no hover, no clique
   * e pelo teclado.
   *
   * É só o MENU: cada uma continua sendo uma aba, com o endereço dela
   * (`/admin/<id>`) e a linha dela na matriz de permissões. O submenu mostra só
   * as que a pessoa pode ver, e some quando ela não pode ver nenhuma.
   */
  grupo?: "ferramentas";
};

/**
 * O mapa das abas, ditado por Victor em 17/08: **Suporte mexe com os dados do
 * aluno; Admin mexe nas configurações do site e das ferramentas.**
 *
 * Duas áreas do Bubble ficaram FORA por decisão dele no mesmo dia — Site de
 * Questões e Gerenciador de Estudos —, e não por esquecimento: os dois módulos
 * não migram (o ROADMAP registra isso em "Fora de escopo — decidido, não
 * pendente"), e quem ainda usa passa a usar o CFC Trilha de Estudos. Seis dos
 * dezesseis atalhos do painel antigo eram deles.
 *
 * Aba sem entrada em `CONTEUDO` desenha o que ela vai fazer e o que ela espera. É
 * de propósito, e é o oposto de widget vazio: número inventado destrói a
 * confiança nos números reais, mas dizer "isto ainda não existe, e falta X" é
 * informação que o operador e quem constrói precisam ter.
 *
 * "Está pronta?" é derivado de `CONTEUDO`, e não um campo à parte, porque um
 * booleano ao lado da lista de componentes é uma segunda verdade: bastaria
 * marcar `pronta` e esquecer o componente para a aba prometer o que não entrega.
 */
const ABAS: Aba[] = [
  {
    id: "painel",
    rotulo: "Painel",
    area: "admin",
    faz: "As métricas do hub, medidas na hora.",
  },
  {
    id: "alunos",
    rotulo: "Alunos",
    area: "suporte",
    // Construída em leitura (000056). Criar usuário, editar cadastro e liberar
    // primeiro acesso entram um por vez — a própria tela diz isso ao operador,
    // então não há `espera` aqui para não haver duas versões do mesmo recado.
    faz: "Buscar um aluno e ver o caso dele inteiro.",
  },
  // Não existe aba "Acessos", e a ausência é decisão. Corrigir prazo, cancelar
  // fonte e liberar produto vivem DENTRO de Alunos, porque é assim que o operador
  // pensa: ele tem um aluno com um problema, não um acesso solto. É também como a
  // tela do Bubble faz. Uma aba própria seria uma segunda porta para a mesma
  // ação, e a segunda porta é a que fica desatualizada.
  {
    id: "triagem",
    rotulo: "Triagem",
    area: "suporte",
    faz: "A fila do que a esteira não conseguiu aplicar.",
    // Construída na 000061. Sem `itens`/`espera`, como a aba Alunos: quem tem
    // tela não descreve o que falta, mostra o que faz.
  },
  {
    id: "faculdade",
    rotulo: "Faculdade",
    area: "suporte",
    // Construída em 31/08 (000130-000136), com a fila do balcão. Sem
    // `itens`/`espera` para a parte que existe: quem tem tela mostra o que faz.
    //
    // O que ela AINDA não tem, e por que não está descrito aqui: "definir turma
    // atual" e "liberar tentativas" existem no banco (`matriculas.turma_id`,
    // `pos_liberacoes`) e não têm tela. Descrevê-las aqui daria duas versões do
    // mesmo recado — a aba mostraria o que faz E o que falta na mesma respiração.
    // Ficam nomeadas no runbook, que é onde a próxima pessoa procura.
    faz: "A fila do balcão: aprovar documento enviado e entregar o pedido.",
  },
  {
    id: "docentes",
    rotulo: "Docentes",
    // `suporte`, e isto NÃO é detalhe de arrumação. Até 21/09/2026 os docentes
    // eram a sétima sub-aba de Faculdade, que é de suporte — então a equipe de
    // suporte alcança essa tela hoje. Pôr a aba nova em `admin` a tiraria
    // deles, e reduzir alcance é ato explícito, nunca efeito colateral de uma
    // mudança de navegação (o princípio 1, do lado de quem opera).
    area: "suporte",
    // Subiu de sub-aba para aba de topo a pedido de Victor em 21/09/2026.
    //
    // O endereço mudou de `/admin/faculdade/docentes` para `/admin/docentes`,
    // e o link antigo cai no padrão de Faculdade (a fila do balcão) em vez de
    // dar erro — a sub-aba desconhecida já tinha esse desfecho. Vale saber
    // porque nesta casa a sub-rota existe para o operador mandar o link a um
    // colega: quem tiver o endereço velho salvo vai parar noutra tela sem
    // aviso.
    //
    // Por que ela sai de Faculdade: o docente deixou de ser assunto só da pós.
    // A Central de Professores passou a ser a fonte da verdade do cadastro de
    // professor (decisão de Victor, 21/09), e `pos_docentes` é o consumidor
    // disso no hub — não mais um cadastro solto dentro do Admin Pós.
    faz: "O corpo docente: nome, contato e nível, e em que disciplinas cada um está.",
  },
  {
    id: "sugestoes",
    rotulo: "Sugestões",
    // Atendimento, e não Configuração: quem registra é o CS, no meio do
    // atendimento ao aluno. Construída na 000301 (G9 do Plano de Melhorias de
    // Produtos) — Victor decidiu em 24/09 que as sugestões moram NO HUB, e não
    // na lista do ClickUp nem só na Central.
    area: "suporte",
    faz: "O que os alunos pedem: o CS registra, Produtos trata e responde, e a que pede professor vai para a Central.",
  },
  {
    id: "catalogo",
    rotulo: "Catálogo",
    area: "admin",
    // Construída em 16/09 (000253-000256). Sem `itens`/`espera`, como as outras
    // que existem: quem tem tela não descreve o que falta, mostra o que faz.
    //
    // O `espera` que ficou aqui de 17/08 a 16/09 era o desenho inteiro, e vale
    // registrar que ele foi ATENDIDO em vez de apagado: *"é a aba com mais
    // risco de estrago silencioso: mexer em id externo desvia provisionamento
    // de venda, e mexer em card faz curso perder ferramenta na tela do aluno.
    // Precisa de confirmação e de registro de quem mudou o quê."*
    //
    // A confirmação é o motivo obrigatório que as RPCs cobram em todo ato
    // irreversível — e a recusa já vem com o NÚMERO de alunos alcançados, que
    // é o que transforma "tem certeza?" numa pergunta respondível. O registro é
    // `catalogo_mudancas`, com a fotografia do antes e do depois.
    faz: "Os produtos, o que cada venda libera, e o que cada curso mostra.",
  },
  {
    // O id continua `avaliacoes` — é a chave da matriz de permissões e o
    // endereço (`/admin/avaliacoes`) —, e o NOME virou «Feedbacks» em 30/09
    // (000332), quando as pesquisas de satisfação vieram morar aqui, ao lado
    // das avaliações das ferramentas. Trocar o id mexeria nas funções, nas
    // concessões e nos links mandados a colegas por um ganho que é só de nome.
    id: "avaliacoes",
    rotulo: "Feedbacks",
    area: "admin",
    // Construída na 000327, a pedido de Victor em 29/09, logo depois de as
    // avaliações das ferramentas passarem a morar no hub (000326, F9). Ela nasce
    // com o nível que cada grupo já tinha no Catálogo: a ficha de cada
    // ferramenta já mostrava as avaliações a quem vê o Catálogo, então a aba
    // nova não amplia quem vê. As pesquisas vieram do Catálogo pela mesma razão
    // (000332): os quatro grupos tinham o mesmo nível nas duas abas.
    faz: "O que os alunos dizem: as avaliações de cada ferramenta, com a meta e a comparação, e as pesquisas de satisfação de cada curso.",
  },
  {
    id: "conteudo",
    rotulo: "Conteúdo",
    area: "admin",
    grupo: "ferramentas",
    // Construída na 000073/000074, para o IR PRO. Sem `itens`/`espera`, como
    // Alunos e Triagem: quem tem tela não descreve o que falta, mostra o que faz.
    //
    // O MEI PRO aparece no seletor dizendo que ainda está no Bubble. O motor de
    // conteúdo é o mesmo — falta a carga e a decisão sobre o livro-caixa (124 mil
    // lançamentos, clientes com login próprio). Ver o cabeçalho da 000073.
    faz: "As seções, páginas e blocos das ferramentas de conteúdo.",
  },
  {
    id: "ocupacoes",
    rotulo: "Ocupações",
    area: "admin",
    grupo: "ferramentas",
    // Construída na 000086, com a lista do Bubble carregada (502 linhas). Sem
    // `itens`/`espera`: quem tem tela mostra o que faz.
    faz: "As atividades que podem (e não podem) ser MEI.",
  },
  {
    id: "parametros",
    rotulo: "Parâmetros",
    area: "admin",
    grupo: "ferramentas",
    // Construída em 25/08, a pedido de Victor. As RPCs já existiam e estavam
    // provadas desde a 000098 (MEI) e a 000108 (folha) — o que faltava era a
    // tela, e sem ela a virada do ano era SQL à mão contra produção.
    //
    // Uma aba para as DUAS tabelas porque o trabalho é um: virou o ano, os
    // números mudam. Duas abas fariam a equipe visitar dois lugares para o mesmo
    // ato, e é assim que se esquece o segundo — ainda mais com o salário mínimo,
    // que hoje mora nas duas.
    faz: "Os números que expiram em janeiro: salário mínimo, tabelas de INSS e IRRF, e os parâmetros do MEI.",
  },
  {
    id: "calendario",
    rotulo: "Calendário",
    area: "admin",
    grupo: "ferramentas",
    // Carregado na 000110 a partir do app da Lovable, com a regra DEDUZIDA das
    // datas que ele mostrava: 19 das 20 fecham, e nenhuma nasce confirmada.
    faz: "As regras de vencimento das obrigações federais, os feriados de exceção e os decretos.",
  },
  {
    id: "legalizacao",
    rotulo: "Legalização",
    area: "admin",
    grupo: "ferramentas",
    // Carregado na 000115 a partir do app da Lovable: 27 juntas com 81 links de
    // portal de governo e 68 modelos. Nenhum link nasce conferido — a planilha
    // de origem já tinha o Sergipe funcionando só por redirect —, e o botão de
    // confirmar carimba QUEM e QUANDO pelo servidor.
    faz: "As 27 juntas comerciais: os links dos sistemas oficiais, com quem conferiu e quando, e os modelos de contrato.",
  },
  {
    id: "concursos",
    rotulo: "Concursos",
    area: "admin",
    grupo: "ferramentas",
    // Carregado na 000120 a partir do app da Lovable: 256 concursos, dos quais
    // 194 NÃO existem na planilha de origem — foram digitados na tela de Admin
    // dele, em sessões de trabalho de abril a agosto de 2026 (o intervalo
    // mediano entre dois cadastros é de 4,5 minutos, que é ritmo humano).
    //
    // Sem esta aba o catálogo congelaria no dia da virada, e aqui a dívida vence
    // em SEMANAS: 209 dos 256 concursos já estão com a inscrição encerrada. É a
    // pendência 8 do Gerenciador de Estudos com o prazo encurtado.
    faz: "O catálogo de concursos contábeis: prazo de inscrição, remuneração, edital, e quem conferiu cada um.",
  },
  {
    id: "forofacil",
    rotulo: "ForoFácil",
    area: "admin",
    grupo: "ferramentas",
    // Carregado na 000169 a partir do app da Lovable: 27 TJs + 6 TRFs com 403
    // documentos de credenciamento. O dado da origem foi revisado 4 vezes, todas
    // em 18/03/2026, e trouxe o cadastro de Rondônia apontando para o Tribunal
    // ELEITORAL. Nenhum tribunal nasce conferido; os 6 TRFs entram com a nota
    // "lista genérica" à vista do aluno até a equipe conferir.
    faz: "Os 33 tribunais do ForoFácil: os links de cadastro como perito, a lista de documentos de cada um, e quem conferiu.",
  },
  {
    id: "radar",
    rotulo: "Radar Simples",
    area: "admin",
    grupo: "ferramentas",
    // Construída na 000190, junto das RPCs — e não antes delas, porque "RPC
    // de tela sem tela é RPC não provada": a `mei_atualizar_parametros`
    // (000098) passou um dia inteiro quebrada pelo `safeupdate` por ter
    // nascido sem chamador, e só a aba Parâmetros revelou.
    //
    // O catálogo de CNAEs vive aqui porque desde a 000189 ele é UM só para o
    // hub inteiro: o Radar enquadra por ele e as ocupações do MEI apontam
    // para ele. Antes eram duas listas, com 37 descrições divergentes para o
    // mesmo código — cada tela mostrando um nome, e nenhuma errada o
    // suficiente para alguém notar.
    faz: "O catálogo de CNAEs do hub inteiro, as alíquotas estimadas de CBS e IBS, e as 30 faixas dos cinco anexos do Simples.",
  },
  {
    id: "cursos-gratuitos",
    rotulo: "Cursos gratuitos",
    area: "admin",
    // Construída na 000381, com as funções da área administrativa do certificados-cf (os cursos
    // gratuitos ao vivo, que vieram para o hub na 000380). Ela nasceu só para o master: lá todo
    // e-mail da casa era administrador, e aqui quem decide qual grupo vê é um master, em Permissões.
    //
    // Fica DEPOIS do submenu «Ferramentas», e não logo depois de Feedbacks: o submenu ocupa o lugar
    // onde as oito abas ficavam (a decisão de 30/09, que `menu-do-painel.mjs` vigia).
    faz: "Os cursos gratuitos ao vivo: os dados, a agenda, os links, os inscritos e os certificados.",
  },
  {
    id: "correcao-cfc",
    rotulo: "Correção CFC",
    area: "admin",
    // Construída na 000384, com as cinco seções do `admin-correcao-cfc` (Flutter, no Fly.io). Nasceu só
    // para o master: lá qualquer conta criada no admin alterava o quadro público; aqui quem decide qual
    // grupo vê e qual edita é um master, em Permissões.
    faz: "A correção ao vivo do Exame de Suficiência: corrigir, ligar os cadernos, o gabarito dos concorrentes, o quadro público e as edições.",
  },
  {
    id: "sentinela",
    rotulo: "Sentinela",
    area: "admin",
    // Construída na 000397 (a fase 3a do Sentinela): as telas do app de lá, só leitura, sobre a cópia contínua.
    // Nasceu só para o master, como a Correção CFC. Até a virada ela mostra o acervo inteiro (os alvos de todos os
    // alunos, como o administrador de lá); na virada, o mesmo conjunto vai para o aluno, com o recorte dele.
    faz: "O monitoramento de riscos da Pós Auditoria, lido da cópia do app de lá: terceiros, CPFs, dossiês, alertas e consumo.",
  },
  {
    id: "aplicativo",
    rotulo: "Aplicativo",
    area: "admin",
    faz: "O que o app das lojas mostra.",
    espera:
      "O app fala com a API em Dart no Fly.io, não com o hub. Configurar o app daqui só faz sentido depois que essa API apontar para cá — é a Fase 5.",
  },
  {
    id: "permissoes",
    rotulo: "Permissões",
    area: "admin",
    faz: "Quem vê e quem edita cada aba, por grupo e por pessoa.",
  },
  {
    id: "auditoria",
    rotulo: "Auditoria",
    area: "admin",
    faz: "Quem fez o quê, e por quê.",
    espera:
      "Boa parte do material já existe e ninguém lê: `eventos_provisionamento` guarda toda concessão (inclusive as manuais, com motivo) e `correcoes_prazo` guarda ajuste à mão. Falta a tela que junta isso por aluno e por operador.",
  },
  /*
   * NÃO há aba "Declarações", e a ausência é decisão de Victor em 21/09.
   *
   * Ela era um PLACEHOLDER de 17/08, escrito quando o PDF ainda saía do n8n: a
   * aba seria "o botão e o registro de emissão, não um gerador novo". O
   * trabalho foi feito e foi feito em OUTRO lugar — desde 16/09 o hub emite
   * sozinho (`emitir-documento`, com o Gotenberg), e quem clica o botão é o
   * balcão da Faculdade, na fila da Secretaria. Medido em 21/09: **4.064
   * declarações e 2.274 certificados** já emitidos.
   *
   * Uma aba prometendo o que já existe em outra tela é a "segunda porta para a
   * mesma ação" desta casa antes mesmo de nascer — e, pior, ela ocupava um
   * lugar no menu dizendo "isto falta" sobre algo pronto.
   *
   * Está escrito aqui, e não só no commit, porque o modo de falha de um
   * placeholder removido é alguém repô-lo achando que ninguém fez.
   */
];

/**
 * As abas que existem. A ausência aqui é o que faz a aba mostrar "o que falta".
 *
 * Recebem o operador porque parte da tela depende do NÍVEL na aba (ver ou
 * editar). Isso é só o desenho: quem recusa é o banco, em cada função.
 */
const CONTEUDO: Record<string, (op: Operador) => ReactNode> = {
  painel: () => <Dashboard />,
  // «Ver como» é só do MASTER (000335): o botão só aparece para ele, e quem
  // recusa continua sendo `ver_como_iniciar`, pela conta logada.
  alunos: (op) => <SuporteAlunos podeVerComo={op.admin} />,
  triagem: () => <SuporteTriagem />,
  faculdade: () => <AdminFaculdade />,
  docentes: () => <AdminPosDocentes />,
  // Excluir sugestão é só do MASTER (000319): o botão só aparece para ele, e
  // quem recusa continua sendo `cs_sugestao_excluir`.
  sugestoes: (op) => <AdminSugestoes podeExcluir={op.admin} />,
  // Quem só VÊ o Catálogo (o grupo CS, 000295) recebe a tela em modo leitura.
  // É conforto, não trava: quem recusa a escrita é cada função `cat_admin_*`.
  // A página do produto aponta para as pesquisas do curso, que moram na aba
  // Feedbacks desde a 000332 — só para quem vê aquela aba.
  catalogo: (op) => (
    <AdminCatalogo
      podeEditar={nivelNaAba(op, "catalogo") === "editar"}
      veFeedbacks={nivelNaAba(op, "avaliacoes") !== "nenhum"}
    />
  ),
  // Os atos de escrita da aba são a meta de uma ferramenta e, desde a 000332,
  // abrir, encerrar e convidar numa pesquisa; quem recusa a de quem só vê é cada
  // função. O link para o Catálogo só aparece a quem vê aquela aba — o painel
  // dela recusaria.
  avaliacoes: (op) => (
    <AdminFeedbacks
      podeEditar={nivelNaAba(op, "avaliacoes") === "editar"}
      veCatalogo={nivelNaAba(op, "catalogo") !== "nenhum"}
    />
  ),
  // Quem só VÊ recebe a tela sem os formulários (o aviso do alto é o do painel); quem recusa a
  // escrita é cada função `cg_admin_*`.
  "cursos-gratuitos": () => <AdminCursosGratuitos />,
  // Quem só VÊ recebe a tela sem os campos de escrita; quem recusa é cada função `corr_admin_*`.
  "correcao-cfc": () => <AdminCorrecaoCfc />,
  sentinela: () => <AdminSentinela />,
  conteudo: () => <AdminConteudo />,
  ocupacoes: () => <AdminOcupacoes />,
  parametros: () => <AdminParametros />,
  calendario: () => <AdminCalendario />,
  legalizacao: () => <AdminLegaliza />,
  concursos: () => <AdminConcursos />,
  forofacil: () => <AdminForoFacil />,
  radar: () => <AdminRadar />,
  permissoes: () => <AdminPermissoes />,
};

/**
 * Quem só VÊ a aba (000297) é avisado uma vez, no alto — e não botão a botão.
 * A tela continua a mesma, e quem recusa a escrita é o banco, com a frase dele.
 * Sem este aviso, o primeiro sinal de "você só pode ver" seria um erro no meio
 * de um formulário já preenchido.
 *
 * O Catálogo tem aviso próprio (e esconde os controles de edição), por isso
 * fica de fora daqui — dois avisos dizendo o mesmo na mesma tela é ruído. A aba
 * Feedbacks também: nas avaliações o único ato de escrita é a meta, e o bloco da
 * meta diz, no lugar do formulário, quem pode mudá-la; nas pesquisas a seção
 * avisa no alto, como o Catálogo.
 */
// O Sentinela é só leitura para todo mundo até a virada, e diz isso no alto da própria aba.
const LEITURA_PROPRIA = new Set(["catalogo", "avaliacoes", "sentinela"]);

function AvisoSoLeitura({ rotulo }: { rotulo: string }) {
  return (
    <p role="note" data-aba-so-leitura className="mb-5 rounded-cf border border-borda bg-chip px-4 py-3 text-sm text-tinta">
      Você pode <strong>ver</strong> a aba {rotulo}, não editar: as mudanças daqui são recusadas pelo
      banco. Quem dá edição é um master, em Admin › Permissões.
    </p>
  );
}

/**
 * A aba aparece para esta pessoa? Espelho do banco, e é só isso: o nível sai de
 * `minhas_permissoes()` (000295). O Painel é de todo operador; aba sem linha em
 * `perm_areas` (Aplicativo, Auditoria) é de admin, porque `nivelNaAba` devolve
 * "nenhum" para ela.
 */
function abaVisivel(op: Operador, a: Aba): boolean {
  if (a.oculta) return false;
  if (a.id === "painel") return op.painel;
  return nivelNaAba(op, a.id) !== "nenhum";
}

export default function Admin() {
  const { aba: abaUrl } = useParams();
  const estadoOperador = useOperador();

  if (estadoOperador.fase === "carregando") return <Carregando />;

  if (estadoOperador.fase === "erro") {
    return (
      <Aviso titulo="Não deu para confirmar seu acesso">
        <p>
          Não conseguimos ler seus papéis agora. Tente recarregar a página; se
          continuar, é sinal de problema de conexão com o banco.
        </p>
      </Aviso>
    );
  }

  // Quem não é operador vai para a área do aluno. Não é a trava — é para não
  // deixar ninguém olhando uma tela vazia sem entender por quê.
  if (!estadoOperador.operador.painel) return <Navigate to="/" replace />;

  // Aba oculta é INALCANÇÁVEL, não só invisível: sem esta metade, quem tem o
  // link antigo (ou o histórico do navegador) continua abrindo a tela, e
  // "desligada" seria só uma promessa do menu.
  const aba = ABAS.find((a) => a.id === abaUrl && abaVisivel(estadoOperador.operador, a));
  if (!aba) return <Navigate to="/admin/painel" replace />;

  return (
    <div className="min-h-screen bg-fundo">
      <TopoAdmin ativa={aba.id} op={estadoOperador.operador} />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {nivelNaAba(estadoOperador.operador, aba.id) === "ver" && !LEITURA_PROPRIA.has(aba.id) && (
          <AvisoSoLeitura rotulo={aba.rotulo} />
        )}
        {CONTEUDO[aba.id]
          ? CONTEUDO[aba.id](estadoOperador.operador)
          : <AbaEmConstrucao aba={aba} />}
      </main>
    </div>
  );
}

/**
 * Topo do painel: marca, aba ativa em pill laranja e a saída para a área do
 * aluno.
 *
 * A barra rola na horizontal no celular em vez de quebrar em duas linhas — com
 * dez abas, quebrar empurra o conteúdo para baixo da dobra em toda visita.
 *
 * Sobre navy, cor de marca. `text-tinta`, `text-cinza` e `border-borda` são
 * semânticas: trocam com o tema e sumiriam aqui, porque este fundo é escuro nos
 * dois. É a pegadinha que o CLAUDE.md registra.
 *
 * O `overflow: visible` no cabeçalho é o que deixa o submenu de Ferramentas
 * existir. A utility `painel-navy` corta o que passa da borda
 * (`overflow: hidden`), e o submenu PENDE do cabeçalho sobre a página. Aqui não
 * há o que cortar: o quadriculado do `::before` tem o tamanho do cabeçalho, e os
 * gradientes são fundo. Estilo na linha, e não classe, porque ele tem de vencer a
 * utility sem depender da ordem em que o Tailwind as escreve.
 */
function TopoAdmin({ ativa, op }: { ativa: string; op: Operador }) {
  // Cada um vê as abas que o banco diz que pode ver (`minhas_permissoes`,
  // 000295) — o Suporte as dele, Produtos o Catálogo, o admin todas. É
  // espelho: o banco recusa sozinho.
  const visiveis = ABAS.filter((a) => abaVisivel(op, a));

  // A barra do master passa da largura (medido em 04/11: 421 px a mais, em qualquer janela, porque o cabeçalho
  // tem a largura do conteúdo), e as últimas abas — Sentinela, Aplicativo, Auditoria, Permissões — ficavam fora
  // da vista, com a marca de aba ativa escondida. Ao abrir uma aba, a barra rola até ela. Mexe só na rolagem
  // DA BARRA (`scrollLeft`), e não chama `scrollIntoView`, que rolaria a página também. Mede de novo quando a fonte
  // chega: antes dela os rótulos são mais estreitos, e a conta de antes deixava a Permissões meio fora (medido).
  // A aba ativa dentro do submenu fechado (`hidden`) não tem caixa: quem a representa na barra é o botão
  // «Ferramentas».
  const barra = useRef<HTMLElement>(null);
  useEffect(() => {
    const rolar = () => {
      const n = barra.current;
      if (!n) return;
      const a =
        [...n.querySelectorAll<HTMLElement>('[aria-current="page"]')].find((e) => !e.closest("[hidden]")) ??
        n.querySelector<HTMLElement>("[data-ferramenta-atual]")?.closest<HTMLElement>("button");
      if (!a) return;
      const rn = n.getBoundingClientRect();
      const ra = a.getBoundingClientRect();
      if (ra.right > rn.right) n.scrollLeft += ra.right - rn.right + 16;
      else if (ra.left < rn.left) n.scrollLeft -= rn.left - ra.left + 16;
    };
    rolar();
    void document.fonts?.ready.then(rolar);
  }, [ativa]);

  // Os rótulos dos blocos do menu deixaram de ser "Suporte" e "Admin" na 000297:
  // eram os nomes de dois PAPÉIS, e os papéis morreram. O que sobra é o que cada
  // bloco FAZ — atender o aluno e configurar o hub —, e quem vê cada aba é a
  // matriz, não o bloco.
  const grupos: Array<{ area: Area; titulo: string }> = [
    { area: "suporte", titulo: "Atendimento" },
    { area: "admin", titulo: "Configuração" },
  ];

  return (
    // `data-topo-fixo`: este cabeçalho é mais alto que o Topo do aluno, e é por
    // ele que `rolarAte` desconta a rolagem aqui dentro (lib/rolarPara.ts).
    <header
      className="painel-navy sticky top-0 z-40 shadow-[0_4px_18px_-8px_rgba(5,13,28,0.6)]"
      style={{ overflow: "visible" }}
      data-topo-fixo
    >
      <div className="mx-auto max-w-6xl px-4 pt-3 sm:px-6">
        <div className="flex items-center gap-3">
          <LogoCF className="h-6" />
          <span className="rounded-full bg-laranja/20 px-2.5 py-0.5 text-xs font-bold tracking-wide text-laranja-claro uppercase">
            Operação
          </span>
          <Link
            to="/"
            className="ml-auto inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-semibold text-navy-mute transition hover:bg-navy-ink/10 hover:text-navy-ink"
          >
            Ver como aluno
          </Link>
        </div>

        {/* Atendimento e Configuração lado a lado, cada um com o seu rótulo. O rótulo é o
            que faz a divisão existir para quem usa: sem ele, dez abas em fila
            são dez atalhos sem hierarquia, e o operador não sabe qual mexe em
            dado de aluno. */}
        <nav ref={barra} className="-mx-4 mt-2 flex gap-5 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          {grupos.map((g) => {
            const doGrupo = visiveis.filter((a) => a.area === g.area);
            if (doGrupo.length === 0) return null;
            const ferramentas = doGrupo.filter((a) => a.grupo === "ferramentas");
            return (
              <div key={g.area} className="shrink-0">
                <p className="px-1 pb-1 text-[0.65rem] font-bold tracking-widest text-navy-mute uppercase">
                  {g.titulo}
                </p>
                <div className="flex gap-1">
                  {doGrupo.map((a) => {
                    if (a.grupo !== "ferramentas") return <LinkDaAba key={a.id} aba={a} ativa={ativa} />;
                    // O item «Ferramentas» entra no lugar da PRIMEIRA delas, e
                    // nenhuma aparece solta na barra.
                    return a === ferramentas[0] ? <MenuFerramentas key="ferramentas" abas={ferramentas} ativa={ativa} /> : null;
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

/** Uma aba solta na barra. */
function LinkDaAba({ aba: a, ativa }: { aba: Aba; ativa: string }) {
  return (
    <Link
      to={`/admin/${a.id}`}
      aria-current={ativa === a.id ? "page" : undefined}
      className={`inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-semibold whitespace-nowrap transition ${
        ativa === a.id ? "bg-laranja text-azul" : "text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
      }`}
    >
      {a.rotulo}
      {/* O ponto diz "ainda não existe" sem ocupar uma palavra.
          Some na aba ativa, onde a própria tela explica. */}
      {!CONTEUDO[a.id] && ativa !== a.id && (
        <span aria-label="ainda não construída" className="size-1.5 rounded-full bg-navy-mute/50" />
      )}
    </Link>
  );
}

/**
 * «Ferramentas» — o item do menu que guarda as oito abas de configuração das
 * ferramentas do aluno (pedido de 30/09). Cada uma continua sendo uma aba, com o
 * endereço e a linha dela na matriz; o que mudou é que elas moram numa lista que
 * abre daqui. Com uma delas aberta, o item fica laranja e diz qual é: a barra
 * é o que fica à vista no alto de toda tela, e Conteúdo e Radar Simples nem têm
 * título próprio.
 *
 * ## É um DISCLOSURE, e não um `role="menu"`
 * É o padrão de navegação do WAI-ARIA: um botão com `aria-expanded` que mostra
 * uma lista de LINKS. O Tab percorre os links na ordem, o Esc fecha e devolve o
 * foco ao botão, e as setas andam entre eles. Um `role="menu"` prometeria ao
 * leitor de tela o comportamento de um menu de aplicativo, que isto não é.
 *
 * ## Abre no hover, no clique e no teclado
 * O hover só vale para MOUSE (o `pointerType`): no toque não existe hover, e o
 * toque dispara um `pointerenter` antes do clique — tratado como hover, ele
 * abriria a lista e o clique a fecharia no mesmo gesto. O hover fecha com
 * atraso (200 ms): o caminho do botão até a lista passa por um vão de 6px, e sem
 * o atraso ela fecharia no meio dele. O clique FIXA a lista aberta, o segundo
 * clique fecha, e o clique fora também.
 *
 * ## Por que a lista é medida e posicionada à mão
 * A barra das abas rola na horizontal (`overflow-x-auto`), e o que fosse
 * posicionado por ela seria cortado — com `overflow-x` diferente de `visible`, o
 * `overflow-y` vira `auto` também. A lista escapa porque o bloco que a contém é o
 * do cabeçalho, ACIMA da barra: nada entre os dois é posicionado, e o que tem o
 * bloco de contenção fora de uma caixa que rola não é cortado por ela. Antes de
 * pôr `relative` em qualquer coisa entre este componente e o cabeçalho, leia
 * isto: a lista voltaria a ser cortada, sem erro nenhum. A posição sai da medida
 * do botão, relativa a esse bloco, e é refeita quando a barra rola ou a janela
 * muda de tamanho.
 */
function MenuFerramentas({ abas, ativa }: { abas: Aba[]; ativa: string }) {
  const idLista = useId();
  const [modo, setModo] = useState<"fechado" | "hover" | "fixo">("fechado");
  const [posicao, setPosicao] = useState({ left: 0, top: 0 });
  const envoltorio = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const lista = useRef<HTMLUListElement>(null);
  const fechamento = useRef<number | undefined>(undefined);
  // Pedido de foco que só o teclado faz: a seta abre a lista E entra nela.
  const focarAoAbrir = useRef<"primeiro" | "ultimo" | null>(null);
  const { pathname } = useLocation();
  const aberto = modo !== "fechado";
  const atual = abas.find((a) => a.id === ativa);

  const cancelarFechamento = useCallback(() => {
    if (fechamento.current !== undefined) {
      window.clearTimeout(fechamento.current);
      fechamento.current = undefined;
    }
  }, []);

  const fechar = useCallback(() => {
    cancelarFechamento();
    setModo("fechado");
  }, [cancelarFechamento]);

  // Navegar — um item da lista, ou o botão de voltar do navegador — fecha.
  useEffect(() => {
    fechar();
  }, [pathname, fechar]);

  // O temporizador do hover não sobrevive ao componente.
  useEffect(() => cancelarFechamento, [cancelarFechamento]);

  const posicionar = useCallback(() => {
    const b = botao.current;
    const l = lista.current;
    const base = l?.offsetParent;
    if (!b || !l || !(base instanceof HTMLElement)) return;
    const rb = b.getBoundingClientRect();
    const rbase = base.getBoundingClientRect();
    const margem = 8;
    // Alinhada ao botão e sem passar da borda da janela: no celular o botão pode
    // estar no fim de uma barra rolada.
    const esquerda = Math.min(rb.left, document.documentElement.clientWidth - l.offsetWidth - margem);
    setPosicao({ left: Math.max(margem, esquerda) - rbase.left, top: rb.bottom - rbase.top + 6 });
  }, []);

  // Mede ANTES de pintar: a lista nasce no lugar, sem piscar no canto.
  useLayoutEffect(() => {
    if (!aberto) return;
    posicionar();
    const barra = botao.current?.closest("nav");
    window.addEventListener("resize", posicionar);
    barra?.addEventListener("scroll", posicionar, { passive: true });
    return () => {
      window.removeEventListener("resize", posicionar);
      barra?.removeEventListener("scroll", posicionar);
    };
  }, [aberto, posicionar]);

  // O foco entra na lista só quando a SETA a abriu.
  useEffect(() => {
    if (!aberto || !focarAoAbrir.current) return;
    focarNoItem(focarAoAbrir.current);
    focarAoAbrir.current = null;
  }, [aberto]);

  // O clique fora fecha — e o toque fora também.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: globalThis.PointerEvent) => {
      if (!envoltorio.current?.contains(e.target as Node)) fechar();
    };
    document.addEventListener("pointerdown", fora);
    return () => document.removeEventListener("pointerdown", fora);
  }, [aberto, fechar]);

  function links(): HTMLAnchorElement[] {
    return [...(lista.current?.querySelectorAll<HTMLAnchorElement>("a") ?? [])];
  }

  function focarNoItem(qual: "primeiro" | "ultimo") {
    const todos = links();
    (qual === "ultimo" ? todos[todos.length - 1] : todos[0])?.focus();
  }

  function entrar(e: PointerEvent) {
    if (e.pointerType !== "mouse") return;
    cancelarFechamento();
    setModo((m) => (m === "fechado" ? "hover" : m));
  }

  function sair(e: PointerEvent) {
    if (e.pointerType !== "mouse") return;
    cancelarFechamento();
    fechamento.current = window.setTimeout(() => {
      fechamento.current = undefined;
      setModo((m) => (m === "hover" ? "fechado" : m));
    }, 200);
  }

  function alternar() {
    cancelarFechamento();
    setModo((m) => (m === "fixo" ? "fechado" : "fixo"));
  }

  function teclaNoBotao(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const qual = e.key === "ArrowDown" ? "primeiro" : "ultimo";
    cancelarFechamento();
    setModo("fixo");
    // Já aberta pelo hover, o efeito do foco não roda de novo: o foco vai já.
    if (aberto) focarNoItem(qual);
    else focarAoAbrir.current = qual;
  }

  function teclaNaLista(e: KeyboardEvent<HTMLUListElement>) {
    const todos = links();
    const i = todos.indexOf(document.activeElement as HTMLAnchorElement);
    const alvo =
      e.key === "ArrowDown" ? todos[(i + 1) % todos.length]
      : e.key === "ArrowUp" ? todos[(i - 1 + todos.length) % todos.length]
      : e.key === "Home" ? todos[0]
      : e.key === "End" ? todos[todos.length - 1]
      : undefined;
    if (!alvo) return;
    e.preventDefault();
    alvo.focus();
  }

  return (
    <div
      ref={envoltorio}
      className="shrink-0"
      onPointerEnter={entrar}
      onPointerLeave={sair}
      onKeyDown={(e) => {
        if (e.key !== "Escape" || !aberto) return;
        e.preventDefault();
        fechar();
        botao.current?.focus();
      }}
      onBlur={(e) => {
        // O foco saiu do botão e da lista (o Tab depois do último item): fecha.
        if (e.relatedTarget && !envoltorio.current?.contains(e.relatedTarget as Node)) fechar();
      }}
      data-menu-ferramentas
    >
      <button
        ref={botao}
        type="button"
        aria-expanded={aberto}
        aria-controls={idLista}
        onClick={alternar}
        onKeyDown={teclaNoBotao}
        className={`inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-sm font-semibold whitespace-nowrap transition ${
          atual
            ? "bg-laranja text-azul"
            : aberto
              ? "bg-navy-ink/10 text-navy-ink"
              : "text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
        }`}
        data-abrir-ferramentas
      >
        Ferramentas
        {atual && (
          <>
            <span aria-hidden="true">·</span>
            <span className="sr-only">, aberta agora:</span>
            <span data-ferramenta-atual>{atual.rotulo}</span>
          </>
        )}
        <svg
          viewBox="0 0 24 24"
          width="1em"
          height="1em"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`size-3.5 transition-transform ${aberto ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {/* SEM classe de `display` na lista: ela venceria o `display: none` do
          `hidden`, e a lista fechada continuaria na tela (a cicatriz do
          `<dialog>` das avaliações, pelo mesmo mecanismo). */}
      <ul
        ref={lista}
        id={idLista}
        hidden={!aberto}
        onKeyDown={teclaNaLista}
        style={{ left: posicao.left, top: posicao.top }}
        className="absolute z-50 min-w-52 space-y-0.5 rounded-2xl border border-navy-mute/40 bg-azul p-1.5 shadow-[0_18px_40px_-12px_rgba(5,13,28,0.75)]"
        data-submenu-ferramentas
      >
        {abas.map((a) => (
          <li key={a.id}>
            <Link
              to={`/admin/${a.id}`}
              aria-current={ativa === a.id ? "page" : undefined}
              onClick={fechar}
              className={`flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-semibold whitespace-nowrap transition ${
                ativa === a.id
                  ? "bg-laranja text-azul"
                  : "text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink focus-visible:bg-navy-ink/10 focus-visible:text-navy-ink"
              }`}
              data-item-ferramenta={a.id}
            >
              {a.rotulo}
              {!CONTEUDO[a.id] && ativa !== a.id && (
                <span aria-label="ainda não construída" className="size-1.5 rounded-full bg-navy-mute/50" />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Dashboard() {
  const { estado, recarregar, recarregando } = usePainel();

  if (estado.fase === "carregando") return <Carregando />;

  if (estado.fase === "erro") {
    return (
      <SecaoAviso titulo="As métricas não vieram">
        Houve erro ao consultar o banco. Nenhum número é mostrado no lugar —
        painel com número inventado é pior que painel vazio.
      </SecaoAviso>
    );
  }

  // O banco devolve zero linha para quem não é operador. Chegar aqui significa
  // que o papel foi removido entre a checagem e esta consulta.
  if (estado.fase === "negado") {
    return (
      <SecaoAviso titulo="Seu acesso de operador não vale mais">
        O banco recusou a consulta do painel. Se isso não era esperado, alguém
        removeu seu papel.
      </SecaoAviso>
    );
  }

  const p = estado.painel;

  return (
    <div className="space-y-6">
      {/* Os três fatos que decidem se alguém precisa agir agora. */}
      <section className="painel-navy rounded-cf-lg p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-lg font-bold text-navy-ink">Agora</h1>
          <p className="text-sm text-navy-mute">
            medido às {horaDoDado(p.medido_em)} — horário de Brasília
          </p>
          <button
            type="button"
            onClick={recarregar}
            disabled={recarregando}
            className="ml-auto inline-flex min-h-9 items-center gap-2 rounded-full border border-navy-mute/30 px-3.5 text-sm font-semibold text-navy-ink transition hover:bg-navy-ink/10 disabled:opacity-55"
          >
            {recarregando ? "Atualizando…" : "Atualizar"}
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <DestaqueNavy
            rotulo="Alunos com acesso"
            valor={p.alunos_com_acesso}
            nota="Agregado das fontes vigentes, não a contagem de linhas."
          />
          <DestaqueNavy
            rotulo="Eventos hoje"
            valor={p.eventos_hoje}
            nota="O que a esteira entregou desde a meia-noite."
          />
          <DestaqueNavy
            rotulo="Falhas na triagem"
            valor={p.triagem_falhas}
            nota={
              p.triagem_falhas > 0
                ? "Venda que não entrou e está esperando alguém. Veja em Triagem — arquivada com motivo não conta aqui."
                : "Nada travado na esteira."
            }
            atencao={p.triagem_falhas > 0}
          />
        </div>

        {/* O pior caso da esteira não é a fila cheia: é a fila vazia porque nada
            chegou. Fila vazia e "nenhum evento hoje" são indistinguíveis sem
            este aviso, e é dívida conhecida do projeto. */}
        {p.eventos_hoje === 0 && (
          <p className="mt-4 rounded-cf bg-laranja/15 px-3 py-2 text-sm text-laranja-claro">
            Nenhum evento hoje. Pode ser dia parado — ou a esteira parada. As duas
            coisas se parecem daqui: o alarme de silêncio ainda não existe.
          </p>
        )}
      </section>

      <Grupo
        titulo="Esteira"
        descricao="O que chegou das plataformas e o que não deu para aplicar."
      >
        <Metrica rotulo="Eventos em 7 dias" valor={p.eventos_7d} />
        <Metrica
          rotulo="Falhas"
          valor={p.triagem_falhas}
          nota="Não aplicado. Precisa de alguém."
          atencao={p.triagem_falhas > 0}
        />
        <Metrica
          rotulo="Avisos"
          valor={p.triagem_avisos}
          nota="Aplicado, com divergência conhecida."
        />
        <Metrica
          rotulo="Webhooks recusados"
          valor={p.webhooks_rejeitados}
          nota="Payload que nenhum adaptador entendeu."
          atencao={p.webhooks_rejeitados > 0}
        />
      </Grupo>

      <Grupo titulo="Alunos e contas" descricao="Quem existe no hub e quem já entrou nele.">
        <Metrica rotulo="Alunos com acesso" valor={p.alunos_com_acesso} />
        <Metrica rotulo="Perfis" valor={p.perfis} nota="Inclui quem já venceu." />
        <Metrica
          rotulo="Contas criadas"
          valor={p.contas_criadas}
          nota="O hub não está publicado: ninguém foi convidado ainda."
        />
        <Metrica
          rotulo="Vínculos pendentes"
          valor={p.vinculos_pendentes}
          nota="Conta confirmada que não achou o perfil."
          atencao={p.vinculos_pendentes > 0}
        />
      </Grupo>

      <Grupo
        titulo="Acessos"
        descricao="Uma linha por (aluno, produto, transação) — duas compras do mesmo curso são duas fontes, e não duplicidade."
      >
        <Metrica rotulo="Vigentes" valor={p.acessos_vigentes} />
        <Metrica rotulo="Vitalícios" valor={p.acessos_vitalicios} nota="Data nula, nunca data grande." />
        <Metrica
          rotulo="Vencem em 30 dias"
          valor={p.acessos_vencem_30d}
          nota="A fila de renovação. Vitalício não entra."
        />
        <Metrica rotulo="Expirados" valor={p.acessos_expirados} />
        <Metrica rotulo="Cancelados" valor={p.acessos_cancelados} nota="Estorno ou decisão registrada." />
      </Grupo>

      <Grupo titulo="Contratos" descricao="A assinatura tranca a vitrine do curso, nunca o acesso.">
        <Metrica
          rotulo="Assinados"
          valor={p.contratos_assinados}
          nota="Zerados em 12/08 por decisão: todos assinam de novo."
        />
        <Metrica
          rotulo="Produtos que exigem"
          valor={p.produtos_exigem_contrato}
          nota="Ligado nos onze. Não afeta ninguém enquanto o hub não é publicado."
        />
      </Grupo>

      <Grupo titulo="Faculdade e catálogo" descricao="Matrícula da Pós e do MBA, e o que a vitrine tem para mostrar.">
        <Metrica rotulo="Matrículas ativas" valor={p.matriculas_ativas} />
        <Metrica rotulo="Produtos ativos" valor={p.produtos_ativos} />
        <Metrica rotulo="Cards da vitrine" valor={p.cards_total} />
        <Metrica
          rotulo="Senha do Memberkit pendente"
          valor={p.mk_senha_pendente}
          nota="Conta sem senha espelhada nas escolas."
          atencao={p.mk_senha_pendente > 0}
        />
        <Metrica
          rotulo="Falhas na sincronização"
          valor={p.mk_falhas}
          atencao={p.mk_falhas > 0}
        />
      </Grupo>
    </div>
  );
}

function DestaqueNavy({
  rotulo,
  valor,
  nota,
  atencao = false,
}: {
  rotulo: string;
  valor: number;
  nota: string;
  atencao?: boolean;
}) {
  return (
    <div className="tv-card p-4">
      <p className="text-xs font-semibold tracking-wide text-navy-mute uppercase">{rotulo}</p>
      <p
        className={`mt-1 font-display text-3xl font-bold tabular-nums ${
          atencao ? "text-laranja-claro" : "text-navy-ink"
        }`}
      >
        {numero(valor)}
      </p>
      <p className="mt-1 text-xs leading-snug text-navy-mute">{nota}</p>
    </div>
  );
}

function Grupo({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 className="font-display text-base font-bold text-titulo">{titulo}</h2>
      <p className="mt-0.5 max-w-3xl text-sm leading-relaxed text-cinza">{descricao}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

/**
 * Um número e o que ele quer dizer.
 *
 * A `nota` não é enfeite: é o que separa painel de parede de números. "Contas
 * criadas: 1" parece falha do sistema até alguém dizer que o hub não foi
 * publicado; "Contratos assinados: 0" parece defeito até alguém dizer que foram
 * zerados de propósito.
 */
function Metrica({
  rotulo,
  valor,
  nota,
  atencao = false,
}: {
  rotulo: string;
  valor: number;
  nota?: string;
  atencao?: boolean;
}) {
  return (
    <div className={`surface p-4 ${atencao ? "ring-1 ring-laranja/45" : ""}`}>
      <p className="text-xs font-semibold tracking-wide text-cinza uppercase">{rotulo}</p>
      <p
        className={`mt-1 font-display text-2xl font-bold tabular-nums ${
          atencao ? "text-laranja-texto" : "text-titulo"
        }`}
      >
        {numero(valor)}
      </p>
      {nota && <p className="mt-1 text-xs leading-snug text-cinza">{nota}</p>}
    </div>
  );
}

function SecaoAviso({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="surface p-6">
      <h1 className="font-display text-lg font-bold text-titulo">{titulo}</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cinza">{children}</p>
    </div>
  );
}

/**
 * A aba que ainda não existe, dizendo o que vai fazer e o que espera.
 *
 * Deliberadamente não é um "em breve" mudo. Quem abre esta tela é o operador (que
 * precisa saber se pode contar com ela hoje) ou quem vai construí-la (que precisa
 * saber por onde) — e as duas perguntas se respondem com a mesma informação.
 */
function AbaEmConstrucao({ aba }: { aba: Aba }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-bold tracking-widest text-laranja-texto uppercase">
        {aba.area === "suporte" ? "Suporte" : "Admin"}
      </p>
      <h1 className="mt-1 font-display text-2xl font-bold text-titulo">{aba.rotulo}</h1>
      <p className="mt-2 text-base leading-relaxed text-cinza">{aba.faz}</p>

      <div className="surface mt-5 p-5">
        <p className="text-sm font-bold text-titulo">Ainda não construída</p>

        {aba.itens && (
          <ul className="mt-3 space-y-1.5">
            {aba.itens.map((item) => (
              <li key={item} className="flex gap-2 text-sm leading-relaxed text-cinza">
                <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-full bg-laranja" />
                {item}
              </li>
            ))}
          </ul>
        )}

        {aba.espera && (
          <div className="mt-4 border-t border-borda pt-4">
            <p className="text-xs font-bold tracking-wide text-cinza uppercase">O que falta</p>
            <p className="mt-1.5 text-sm leading-relaxed text-cinza">{aba.espera}</p>
          </div>
        )}
      </div>

      <p className="mt-4 text-sm leading-relaxed text-cinza">
        Enquanto esta aba não existe, o caminho é o painel do Bubble, que continua
        ligado. Nada aqui desliga nada lá.
      </p>
    </div>
  );
}
